import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users } from "lucide-react";
import { BarChart, Bar, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, LabelList } from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { tooltipStyle } from "./oversiktConstants";
import { dec, num } from "@/lib/formatters";

const AlderCompareChart = ({ alderData, colors }) => {
  const rows = Object.values(alderData || {}).filter((k) => k.mean_age != null);
  if (rows.length === 0) return null;
  const land = rows.find((r) => r.land_mean_age != null)?.land_mean_age;
  const bars = rows.map((r) => ({ name: r.name, "Gjennomsnittsalder": r.mean_age, median: r.median_age }));
  const years = [...new Set(rows.flatMap((r) => [...r.history, ...(r.framskriving || [])].map((h) => h.year)))].sort();
  const trend = years.map((y) => ({
    year: y,
    ...Object.fromEntries(rows.flatMap((r) => [
      [r.name, y <= r.year ? r.history.find((h) => h.year === y)?.mean_age ?? null : null],
      [`${r.name} (framskrive)`, y >= r.year ? (r.framskriving || []).find((h) => h.year === y)?.mean_age ?? null : null],
    ])),
  }));
  const dep = rows.filter((r) => r.dep_total != null).map((r) => ({ name: r.name, "Barn og unge 0–19 år": r.dep_young, "Eldre 67 år og over": r.dep_old, total: r.dep_total }));
  const landDep = rows.find((r) => r.land?.dep_total)?.land?.dep_total;
  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg lg:col-span-2" data-testid="compare-alder-chart">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <Users className="w-5 h-5 text-[#103766]" />
          Gjennomsnittsalder, aldring og barn og eldre per 100 yrkesaktive {rows[0].year}
        </CardTitle>
        <p className="text-sm text-[#4B647F]">Stipla linje i søylediagramma viser landsgjennomsnittet{land ? ` (${dec(land, 1)} år` : ""}{landDep ? `, barn og eldre per 100 yrkesaktive: ${dec(landDep, 1)})` : land ? ")" : ""}. Stiplede linjer til høyre er SSBs hovedalternativ (14288) fram mot 2050.</p>
      </CardHeader>
      <CardContent className="pt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="h-[300px]" data-testid="compare-alder-bars">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={bars} barCategoryGap="35%">
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
              <XAxis dataKey="name" stroke="#8AA4BF" fontSize={12} />
              <YAxis domain={[30, 55]} stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => `${num(v)} år`} />
              <Tooltip formatter={(v, _n, p) => [`${dec(v, 1)} år (median ${p.payload.median})`, "Gjennomsnittsalder"]} contentStyle={tooltipStyle} />
              {land && <ReferenceLine y={land} stroke="#4B647F" strokeDasharray="4 4" label={{ value: "Landet", position: "insideTopRight", fontSize: 11, fill: "#4B647F" }} />}
              <Bar dataKey="Gjennomsnittsalder" radius={[4, 4, 0, 0]}>
                {bars.map((b, i) => <Cell key={b.name} fill={colors[i % colors.length]} />)}
                <LabelList dataKey="Gjennomsnittsalder" position="top" formatter={(v) => `${dec(v, 1)} år`} fontSize={12} fill="#1F2A36" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="h-[300px]" data-testid="compare-alder-trend">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend}>
              {endringsLinjer(trend)}
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
              <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} ticks={years.filter((y) => y % 5 === 0)} />
              <YAxis domain={["dataMin - 1", "dataMax + 1"]} stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => v.toFixed(0)} />
              <Tooltip formatter={(v) => `${dec(v, 1)} år`} contentStyle={tooltipStyle} />
              <Legend itemSorter={null} />
              {rows.map((r, i) => <Line key={r.name} type="monotone" dataKey={r.name} stroke={colors[i % colors.length]} strokeWidth={2} dot={false} />)}
              {rows.map((r, i) => <Line key={`${r.name}-f`} type="monotone" dataKey={`${r.name} (framskrive)`} stroke={colors[i % colors.length]} strokeWidth={2} strokeDasharray="6 4" dot={false} legendType="none" />)}
            </LineChart>
          </ResponsiveContainer>
        </div>
        {dep.length > 0 && (
          <div className="lg:col-span-2" data-testid="compare-forsorgerbyrde">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">Barn og eldre per 100 i yrkesaktiv alder (20–66 år)</p>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dep} barCategoryGap="35%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                  <XAxis dataKey="name" stroke="#8AA4BF" fontSize={12} />
                  <YAxis stroke="#8AA4BF" fontSize={12} />
                  <Tooltip formatter={(v) => `${dec(v, 1)} per 100`} contentStyle={tooltipStyle} />
                  <Legend itemSorter={null} />
                  {landDep && <ReferenceLine y={landDep} stroke="#4B647F" strokeDasharray="4 4" label={{ value: "Landet", position: "insideTopRight", fontSize: 11, fill: "#4B647F" }} />}
                  <Bar dataKey="Barn og unge 0–19 år" stackId="a" fill="#3F8A86" />
                  <Bar dataKey="Eldre 67 år og over" stackId="a" fill="#E8A34A" radius={[4, 4, 0, 0]}>
                    <LabelList dataKey="total" position="top" formatter={(v) => dec(v, 1)} fontSize={12} fill="#1F2A36" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        <p className="text-xs text-[#8AA4BF] text-center lg:col-span-2">Kjelde: SSB 07459 og 14288 – befolkning etter ettårig alder per 1. januar. Median: {rows.map((r) => `${r.name} ${r.median_age} år`).join(" · ")}</p>
      </CardContent>
    </Card>
  );
};

export default AlderCompareChart;
